'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledDrawer from '@umodeapporg/ui/components/Form/ControlledDrawer';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionTitle } from '@/components/layout/SectionTitle';
import { useAgente } from '@/domain/agente/hooks/useAgente';

import { AgenteComposer } from './AgenteComposer';
import { AgenteConversa } from './AgenteConversa';

const PAINEL_WIDTH = 400;
/** ControlledDrawer stacks at z-[9997]; tooltips inside it must sit above that. */
const TOOLTIP_OVER_DRAWER_CLASS = 'z-[9999]';

/** Side panel version of the agent chat, reachable from every screen. */
export function AgentePainel() {
  const { t } = useTranslation('common');
  const {
    conversa,
    saude,
    painelAberto,
    loading,
    loadingConfirmar,
    fecharPainel,
    enviar,
    confirmar,
    limpar,
    contextoAtual,
  } = useAgente();

  const [texto, setTexto] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (): void => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleEnviar = async (): Promise<void> => {
    const msg = texto.trim();
    if (msg === '' || loading) {
      return;
    }
    setTexto('');
    await enviar(msg, contextoAtual());
    scrollToBottom();
  };

  const handleAtalho = async (atalhoTexto: string): Promise<void> => {
    await enviar(atalhoTexto, contextoAtual());
    scrollToBottom();
  };

  const modeloLigado = saude?.modeloLigado ?? false;
  const statusProvedor =
    modeloLigado && saude !== null ? t('agente.provedorAtivo', { nome: saude.ativo }) : t('agente.semProvedor');

  return (
    <ControlledDrawer open={painelAberto} onClose={fecharPainel} placement="right" size={PAINEL_WIDTH}>
      <div className="flex h-full flex-col bg-surface">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <MaterialSymbol icon="smart_toy" size={20} className="shrink-0 text-brand-emphasis" />
          <div className="flex min-w-0 flex-1 flex-col">
            <SectionTitle>{t('agente.title')}</SectionTitle>
            <p className="truncate text-xs text-foreground-subtle">{statusProvedor}</p>
          </div>
          <ControlledTooltip content={t('agente.limpar')} className={TOOLTIP_OVER_DRAWER_CLASS}>
            <ControlledButton variant="text" tone="neutral" size="sm" className="px-2" onClick={limpar}>
              <MaterialSymbol icon="delete" size={18} />
            </ControlledButton>
          </ControlledTooltip>
          <ControlledTooltip content={t('agente.fechar')} className={TOOLTIP_OVER_DRAWER_CLASS}>
            <ControlledButton variant="text" tone="neutral" size="sm" className="px-2" onClick={fecharPainel}>
              <MaterialSymbol icon="close" size={18} />
            </ControlledButton>
          </ControlledTooltip>
        </div>

        <AgenteConversa
          conversa={conversa}
          loading={loading}
          loadingConfirmar={loadingConfirmar}
          onConfirmar={confirmar}
          onAtalho={handleAtalho}
          onNavegar={fecharPainel}
          bottomRef={bottomRef}
          size="sm"
          className="flex-1 px-4 py-3"
        />

        <div className="border-t border-border px-4 py-3">
          <AgenteComposer
            value={texto}
            onChange={setTexto}
            onEnviar={handleEnviar}
            loading={loading}
            tooltipClassName={TOOLTIP_OVER_DRAWER_CLASS}
          />
        </div>
      </div>
    </ControlledDrawer>
  );
}
